/**
 * QuoteRenderer — quote
 * Elevated card with large quotation mark, centered text, and attribution.
 */
import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";

export function QuoteRenderer({ element, editing, onChange, playing, isPaused, currentTimeSec }) {
  const quote = resolveText(element.quote);
  const attribution = resolveText(element.attribution);
  const role = resolveTextOptional(element.role);

  const quoteBox = element.quote?.boxAnimation ?? null;
  const quoteExit = element.quote?.boxExitAnimation ?? null;
  const attrBox = element.attribution?.boxAnimation ?? null;
  const attrExit = element.attribution?.boxExitAnimation ?? null;
  const roleBox = element.role?.boxAnimation ?? null;
  const roleExit = element.role?.boxExitAnimation ?? null;

  return (
    <SurfaceCard elevated>
      <div style={{ display: "grid", gap: "var(--space-4)", textAlign: "center" }}>
        {/* Decorative quotation mark */}
        <div
          aria-hidden="true"
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "var(--text-4xl)",
            lineHeight: 1,
            color: "var(--color-text-accent)",
            marginBottom: "-var(--space-2)",
          }}
        >
          &ldquo;
        </div>

        <AnimatedField
          boxAnimation={quoteBox}
          boxExitAnimation={quoteExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <Title size="2xl" align="center">
            <EditableText
              value={quote}
              editing={editing}
              onChange={(v) => onChange?.("quote", v)}
              placeholder="Quote text…"
            />
          </Title>
        </AnimatedField>

        {/* Divider */}
        <div
          style={{
            height: "var(--stroke-thin)",
            background: "var(--color-divider)",
            margin: "0 auto",
            width: "40%",
          }}
        />

        <div style={{ display: "grid", gap: "var(--space-1)" }}>
          <AnimatedField
            boxAnimation={attrBox}
            boxExitAnimation={attrExit}
            playing={playing}
            isPaused={isPaused}
            currentTimeSec={currentTimeSec}
          >
            <Text role="large" align="center">
              <EditableText
                value={attribution}
                editing={editing}
                onChange={(v) => onChange?.("attribution", v)}
                placeholder="— Author name"
              />
            </Text>
          </AnimatedField>
          {(role !== null || editing) && (
            <AnimatedField
              boxAnimation={roleBox}
              boxExitAnimation={roleExit}
              playing={playing}
              isPaused={isPaused}
              currentTimeSec={currentTimeSec}
            >
              <Text role="caption" align="center">
                <EditableText
                  value={role ?? ""}
                  editing={editing}
                  onChange={(v) => onChange?.("role", v)}
                  placeholder="Role / Title"
                />
              </Text>
            </AnimatedField>
          )}
        </div>
      </div>
    </SurfaceCard>
  );
}

export default QuoteRenderer;
